import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { useLocation, useParams } from 'react-router-dom'

import axios from '../../dataClient/dataClient'
import { logo } from '../../images/images.js'
import { getPIRFileList, getPIRStatus, getFOIStatus } from '../../dataClient/apiCalls.js'
import { PageWithTitle, Header, Logo, LightBGContainer, PageWithTitleBody } from '../styles/Containers.js'
import { PageTitle } from '../styles/Text.js'
import LoadingError from '../common/LoadingError.js'
import SecurityCodeForm from '../authentication/SecurityCodeForm.js'
import Loading from '../common/Loading.js'
import DownloadFileList from '../files/DownloadFileList.js'
import pirTerminology from '../../util/pirTerminology'

const Page = styled(PageWithTitle)`
  min-width: calc(100vw - 200px);

  @media only screen and (max-width: 1000px) {
    min-width: unset;
    width: 100%;
  }
`

const SecurityCodeContainer = styled(LightBGContainer)`
  max-width: 550px;
  width: 90%;

  @media only screen and (max-width: 1000px) {
    padding: 5% 5%;
  }
`

const ContentContainer = styled(SecurityCodeContainer)`
  width: 90%;
  max-width: 800px;
`

function ResponseDownloadPage() {
  const location = useLocation()
  const params = useParams()

  const route = location.pathname.split('/')[1]
  const recipient = new URLSearchParams(location.search).get('recipient') || ''

  const requestId = route === 'pir' ? params.pirId : params.foiId

  const [details, setDetails] = useState()
  const [fileList, setFileList] = useState([])
  const [errorMessage, setErrorMessage] = useState()
  const [isVerified, setIsVerified] = useState(false)
  const [isExpiredLink, setIsExpiredLink] = useState(false)

  useEffect(() => {
    function getDetails(requestId) {
      if (route === 'pir') {
        getPIRStatus(requestId, recipient)
          .then(({ data }) => setDetails(data.data))
          .catch(e => {
            console.error(`failed to retrieve the ${route.toUpperCase()} status`, requestId, e)

            if (e.response && e.response.data.message === `${route}Id not found: ${requestId}`) {
              setErrorMessage([`The provided ${pirTerminology.getRequestName()} does not exist.`])
            } else {
              setErrorMessage([])
            }
          })
      } else if (route === 'foi') {
        getFOIStatus(requestId)
          .then(({ data }) => {
            const d = data.data
            setDetails(d)
            if (d?.linkExpired === true) setIsExpiredLink(true)
          })
          .catch(e => {
            console.error(`failed to retrieve the ${route.toUpperCase()} status`, requestId, e)

            if (e.response && e.response.data.message === `${route}Id not found: ${requestId}`) {
              setErrorMessage(['The provided Freedom of Information Request does not exist.'])
            } else {
              setErrorMessage([])
            }
          })
      } else {
        setErrorMessage([`${route} is not supported`])
      }
    }

    if (requestId) getDetails(requestId)
  }, [requestId, recipient, route])

  useEffect(() => {
    function getFileList(requestId) {
      if (route === 'pir') {
        getPIRFileList(requestId)
          .then(({ data }) => setFileList(data.data))
          .catch(e => {
            console.error(`failed to retrieve the file list for ${route.toUpperCase()}`, requestId, e)
            setErrorMessage([])
          })
      } else if (route === 'foi') {
        axios
          .get(`/file/${requestId}/response`)
          .then(({ data }) => setFileList(data.data))
          .catch(e => {
            console.error(`failed to retrieve the file list for ${route.toUpperCase()}`, requestId, e)
            setErrorMessage([])
          })
      } else {
        setErrorMessage([`${route} is not supported`])
      }
    }

    if (requestId && isVerified && !(route === 'foi' && isExpiredLink)) getFileList(requestId)
  }, [requestId, isVerified, route, isExpiredLink])

  if (errorMessage !== undefined) {
    return <LoadingError message={errorMessage} />
  }

  const handleVerification = () => setIsVerified(true)

  return (
    <Page>
      <Header>
        <Logo src={logo}></Logo>
        <PageTitle>Download {details ? details.secondaryId : ''} Response</PageTitle>
      </Header>
      <PageWithTitleBody>
        {!details ? (
          <Loading />
        ) : (
          <>
            {!isVerified ? (
              <SecurityCodeContainer>
                <SecurityCodeForm
                  requestId={requestId}
                  requestType={route}
                  secondaryId={details.secondaryId}
                  email={route === 'pir' ? details.requestorEmail : details.requestorEmailMasked}
                  recipient={recipient}
                  status={details.status}
                  onVerify={() => handleVerification()}
                  isExpiredLink={route === 'foi' && isExpiredLink}
                />
              </SecurityCodeContainer>
            ) : (
              <ContentContainer>
                <DownloadFileList type={route} fileList={fileList} requestId={requestId} secondaryId={details.secondaryId} />
              </ContentContainer>
            )}
          </>
        )}
      </PageWithTitleBody>
    </Page>
  )
}

export default ResponseDownloadPage
